import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { ArrowDown } from "components/icons/ArrowDown";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SUMMARY_REPORT_TYPES } from "constants/bookkeeping";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { PeriodType } from "constants/revenueMetrics";
import { useState } from "react";
import { useLazyExportVendorSummaryQuery } from "store/apis/reports";
import { BackendError } from "types/utils/error";

type ExportVendorSummaryFilters = {
  startDate: string;
  endDate: string;
  reportType: PeriodType;
  vendorNames: string[];
  accountingMethod: "ACCRUAL" | "CASH";
  comparisonSummaryType: SUMMARY_REPORT_TYPES;
  categoryList: string[];
  isRelatedParty?: boolean;
};

export const ExportVendorSummary = ({
  filters,
}: {
  filters: ExportVendorSummaryFilters;
}) => {
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();

  const [exportVendorSummary, { isFetching: isExporting }] =
    useLazyExportVendorSummaryQuery();

  const exportSummary = async (
    exportToMail: boolean = false,
    folderId?: string
  ) => {
    try {
      const result = await exportVendorSummary({
        entityId,
        ...filters,
        export_to_mail: exportToMail,
        folderId,
      }).unwrap();

      if (folderId) {
        setSavedDocumentId(result.document_id);
        savedToInkleDocsModal.open();
        return;
      }

      if (!exportToMail && result.download_url) {
        window.open(result.download_url, "_blank");
      }

      successToast({
        message: exportToMail ? "Report sent to email" : "Report exported",
      });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <>
      <Dropdown.Root modal={false}>
        <Dropdown.Trigger asChild className="t-group">
          <div>
            <Button
              size="xsmall"
              customType="primary"
              isLoading={isExporting}
              disabled={isExporting}
            >
              Export
              <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                <ArrowDown color="currentColor" />
              </div>
            </Button>
          </div>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end" sideOffset={4}>
            <ExportOptions
              onLocalDownload={() => exportSummary(false)}
              onMailExport={() => exportSummary(true)}
              onSaveInInkleDocs={selectFolderModal.open}
            />
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => exportSummary(false, folderId)}
        />
      )}
      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType="Vendor Summary"
        />
      )}
    </>
  );
};
